import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { SplitviewComponent, SplitviewPanel, Orientation } from 'dockview-core';
import 'dockview-core/dist/styles/dockview.css';

/**
 * Custom SplitviewPanel implementation for Dash
 */
class DashSplitviewPanel extends SplitviewPanel {
    constructor(id, componentName, contentElement, onInit) {
        super(id, componentName);
        this._contentElement = contentElement;
        this._onInit = onInit;
    }

    getComponent() {
        // Append our content element to the panel's element
        this.element.appendChild(this._contentElement);

        // Call the init callback to signal the panel is ready
        if (this._onInit) {
            this._onInit();
        }

        // Return a component with required interface
        return {
            update: (params) => {
                // Handle parameter updates if needed
            },
            dispose: () => {
                if (this._contentElement.parentNode) {
                    this._contentElement.parentNode.removeChild(this._contentElement);
                }
            }
        };
    }
}

/**
 * DashSplitLayout is a component that provides a simple split panel system.
 * Panels are arranged either horizontally or vertically with resizable dividers.
 */
const DashSplitLayout = (props) => {
    const {
        id,
        setProps,
        children,
        panels = [],
        layout = null,
        savedLayout = null,
        theme = 'dockview-theme-light',
        height = '600px',
        orientation = 'horizontal',
        proportionalLayout = true,
        loading_state = null,
    } = props;

    const containerRef = useRef(null);
    const splitviewApiRef = useRef(null);
    const [panelContainers, setPanelContainers] = useState({});
    const [isReady, setIsReady] = useState(false);
    const disposablesRef = useRef([]);

    // Create a mapping of panel IDs to children
    const childrenMap = useMemo(() => {
        const map = {};
        if (children) {
            const childArray = Array.isArray(children) ? children : [children];
            childArray.forEach((child) => {
                if (child && child.props) {
                    const childId = child.props.id ||
                                   child.props._dashprivate_layout?.props?.id ||
                                   child.key;
                    if (childId) {
                        map[childId] = child;
                    }
                }
            });
        }
        return map;
    }, [children]);

    // Initialize splitview
    useEffect(() => {
        if (!containerRef.current) return;

        const splitview = new SplitviewComponent(containerRef.current, {
            orientation: orientation === 'vertical' ? Orientation.VERTICAL : Orientation.HORIZONTAL,
            proportionalLayout,
            createComponent: (options) => {
                // Create the container element for this panel's content
                const contentElement = document.createElement('div');
                contentElement.style.height = '100%';
                contentElement.style.width = '100%';
                contentElement.style.overflow = 'auto';
                contentElement.style.boxSizing = 'border-box';
                contentElement.className = 'dash-split-panel-content';
                contentElement.setAttribute('data-panel-id', options.id);

                // Return a proper SplitviewPanel instance with init callback
                return new DashSplitviewPanel(options.id, options.name, contentElement, () => {
                    setPanelContainers(prev => ({
                        ...prev,
                        [options.id]: contentElement
                    }));
                });
            },
        });

        splitviewApiRef.current = splitview;

        // Setup event listeners
        const layoutDisposable = splitview.onDidLayoutChange(() => {
            if (setProps) {
                try {
                    setProps({ layout: splitview.toJSON() });
                } catch (err) {
                    console.error('Failed to serialize layout:', err);
                }
            }
        });
        disposablesRef.current.push(layoutDisposable);

        // Load initial panels
        if (panels.length > 0) {
            panels.forEach((panel, index) => {
                const panelId = panel.id || `panel-${index}`;
                splitview.addPanel({
                    id: panelId,
                    component: 'default',
                    size: panel.size,
                    minimumSize: panel.minimumSize,
                    maximumSize: panel.maximumSize,
                });
            });
        }

        if (setProps) {
            setProps({ panelCount: splitview.panels.length });
        }

        setIsReady(true);

        return () => {
            disposablesRef.current.forEach(d => d.dispose());
            disposablesRef.current = [];
            setPanelContainers({});
            splitview.dispose();
            splitviewApiRef.current = null;
            setIsReady(false);
        };
    }, [theme, orientation, proportionalLayout]);

    // Load saved layout
    useEffect(() => {
        if (splitviewApiRef.current && savedLayout) {
            try {
                splitviewApiRef.current.fromJSON(savedLayout);
            } catch (err) {
                console.error('Failed to load layout:', err);
            }
        }
    }, [savedLayout]);

    // Render portals
    const portals = useMemo(() => {
        if (!isReady) return [];

        return Object.entries(panelContainers).map(([panelId, container]) => {
            if (!container || !document.body.contains(container)) {
                return null;
            }

            const child = childrenMap[panelId];
            if (child) {
                return ReactDOM.createPortal(
                    <div style={{ height: '100%', width: '100%', overflow: 'auto' }} key={panelId}>
                        {child}
                    </div>,
                    container
                );
            }
            return ReactDOM.createPortal(
                <div style={{ padding: '1rem', color: 'var(--dv-foreground-color, #333)' }} key={panelId}>
                    <p>Panel: {panelId}</p>
                </div>,
                container
            );
        }).filter(Boolean);
    }, [panelContainers, childrenMap, isReady]);

    return (
        <div id={id} className={theme} style={{ width: '100%', height }}>
            <div
                ref={containerRef}
                className={theme}
                style={{
                    height: '100%',
                    backgroundColor: 'var(--dv-background-color)',
                    border: '1px solid var(--dv-separator-border)'
                }}
            />
            {portals}
        </div>
    );
};

DashSplitLayout.propTypes = {
    id: PropTypes.string.isRequired,
    children: PropTypes.node,
    panels: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        size: PropTypes.number,
        minimumSize: PropTypes.number,
        maximumSize: PropTypes.number,
    })),
    layout: PropTypes.object,
    savedLayout: PropTypes.object,
    theme: PropTypes.string,
    height: PropTypes.string,
    orientation: PropTypes.oneOf(['horizontal', 'vertical']),
    proportionalLayout: PropTypes.bool,
    panelCount: PropTypes.number,
    setProps: PropTypes.func,
    loading_state: PropTypes.shape({
        is_loading: PropTypes.bool,
        prop_name: PropTypes.string,
        component_name: PropTypes.string,
    }),
};

export default DashSplitLayout;
